import {
	type ExtractDocumentTypeFromTypedRxJsonSchema,
	type RxJsonSchema,
	toTypedRxJsonSchema,
} from 'rxdb';

const passengerBookingSchemaLiteral = {
	title: 'passenger booking schema',
	version: 13,
	primaryKey: 'bookingNumber',
	type: 'object',
	properties: {
		id: { type: 'string' },
		bookingCode: { type: 'string' },
		passengers: { type: 'number' },
		bookingNumber: {
			type: 'string',
			maxLength: 100,
		},
		specialNeeds: {
			type: 'string',
			enum: ['NONE', 'WHEELCHAIR_TYPE_1', 'WHEELCHAIR_TYPE_2'],
		},
		passengerList: {
			type: 'array',
			items: {
				type: 'object',
				properties: {
					firstName: { type: 'string' },
					lastName: { type: 'string' },
					email: { type: 'string' },
					phone: { type: 'string' },
					isMainPassenger: { type: 'boolean' },
					specialNeeds: { type: 'string' },
					discountState: { type: 'string' },
				},
			},
		},
		itinerary: {
			type: 'object',
			properties: {
				startDateTime: { type: 'string' },
				endDateTime: { type: 'string' },
				duration: { type: 'number', nullable: true },
				walkTime: { type: 'number', nullable: true },
				walkDistance: { type: 'number', nullable: true },
				airDistance: { type: 'number', nullable: true },
				stops: { type: 'number', nullable: true },
				transfers: { type: 'number', nullable: true },
				luggageDefinition: { type: 'string', nullable: true },
				toddlersDefinition: { type: 'string', nullable: true },
				paymentOptions: {
					type: 'array',
					items: { type: 'string' },
					nullable: true,
				},
				legs: {
					type: 'array',
					items: {
						type: 'object',
						properties: {
							legId: { type: 'string' },
							startDateTime: { type: 'string' },
							startDateTimeFix: { type: 'boolean' },
							endDateTime: { type: 'string' },
							endDateTimeFix: { type: 'boolean' },
							legStatus: { type: 'string', nullable: true },
							delayed: { type: 'boolean' },
							duration: { type: 'number' },
							distance: { type: 'number' },
							mode: {
								type: 'string',
								enum: [
									'WALK',
									'LBT',
									'LBODT',
									'LBODTFLEX',
									'PRODT',
									'TRANSFER_WALK',
									'OTHERS',
									'SPECIAL_SCHOOL_TRAFFIC',
								],
							},
							modeOrigin: { type: 'string', nullable: true },
							route: { type: 'string', nullable: true },
							headsign: { type: 'string', nullable: true },
							routeId: { type: 'string', nullable: true },
							agencyId: { type: 'string', nullable: true },
							agencyName: { type: 'string', nullable: true },
							distributorContactEmail: { type: 'string', nullable: true },
							isBlacklisted: { type: 'boolean', nullable: true },
							tripShortName: { type: 'string', nullable: true },
							tripId: { type: 'string', nullable: true },
							from: {
								type: 'object',
								properties: {
									name: { type: 'string' },
									stopId: { type: 'string', nullable: true },
									lng: { type: 'number' },
									lat: { type: 'number' },
									arrival: { type: 'string', nullable: true },
									departure: { type: 'string', nullable: true },
									locationType: { type: 'string' },
									stopDescription: { type: 'string', nullable: true },
									stopLink: { type: 'string', nullable: true },
									address: { type: 'string', nullable: true },
								},
							},
							to: {
								type: 'object',
								properties: {
									name: { type: 'string' },
									stopId: { type: 'string', nullable: true },
									lng: { type: 'number' },
									lat: { type: 'number' },
									arrival: { type: 'string', nullable: true },
									departure: { type: 'string', nullable: true },
									locationType: { type: 'string' },
									stopDescription: { type: 'string', nullable: true },
									stopLink: { type: 'string', nullable: true },
									address: { type: 'string', nullable: true },
								},
							},
							legGeometry: {
								type: 'object',
								nullable: true,
								properties: {
									points: { type: 'string' },
									length: { type: 'number' },
								},
							},
							intermediateStops: {
								type: 'array',
								items: {
									type: 'object',
									properties: {
										arrival: { type: 'string' },
										departure: { type: 'string' },
										name: { type: 'string' },
									},
								},
							},
							payment: {
								type: 'object',
								nullable: true,
								properties: {
									paymentAmount: { type: 'number', nullable: true },
									currency: { type: 'string', nullable: true },
									paymentForm: { type: 'string', nullable: true },
									paymentState: { type: 'string', nullable: true },
									overwrittenPaymentAmount: { type: 'number', nullable: true },
									paymentInformationAvailable: { type: 'boolean' },
									originalPriceInfo: {
										type: 'object',
										nullable: true,
										properties: {},
										additionalProperties: true,
									},
									overwritingPriceInfo: {
										type: 'object',
										nullable: true,
										properties: {},
										additionalProperties: true,
									},
								},
							},
							pricings: {
								type: 'array',
								items: {
									type: 'object',
									properties: {
										paymentHandlerCompanyName: { type: 'string' },
										paymentHandlerCompanyType: { type: 'string' },
										amount: { type: 'number' },
										pricePerPassenger: { type: 'number' },
										deploymentPrice: { type: 'number' },
										currency: { type: 'string', nullable: true },
										discount: { type: 'string', nullable: true },
										paymentInformationAvailable: { type: 'boolean' },
									},
								},
							},
							timeInfo: {
								type: 'object',
								properties: {
									start: {
										type: 'object',
										properties: {
											startTime: { type: 'string' },
											endTimeWindow: {
												type: 'object',
												nullable: true,
												properties: {},
												additionalProperties: true,
											},
											startTimeForCancellation: {
												type: 'string',
												nullable: true,
											},
											overwrittenStartTimeWindow: {
												type: 'object',
												nullable: true,
												properties: {},
												additionalProperties: true,
											},
											currentlyPredictedStartTime: {
												type: 'string',
												nullable: true,
											},
										},
									},
									end: {
										type: 'object',
										properties: {
											endTime: { type: 'string' },
											endTimeWindow: {
												type: 'object',
												nullable: true,
												properties: {},
												additionalProperties: true,
											},
											overwrittenEndTimeWindow: {
												type: 'object',
												nullable: true,
												properties: {},
												additionalProperties: true,
											},
											currentlyPredictedEndTime: {
												type: 'string',
												nullable: true,
											},
										},
									},
									includedPlannedDelayInMinutes: { type: 'number' },
									startAndEndTimeFixed: { type: 'boolean' },
								},
							},
						},
					},
				},
			},
		},
		payment: {
			type: 'object',
			properties: {
				paymentAmount: { type: 'number', nullable: true },
				currency: { type: 'string', nullable: true },
				paymentForm: { type: 'string' },
				paymentState: { type: 'string' },
				overwrittenPaymentAmount: { type: 'number', nullable: true },
				paymentInformationAvailable: { type: 'boolean' },
			},
		},
		originalPriceInfoAggregate: {
			type: 'object',
			nullable: true,
			properties: {},
			additionalProperties: true,
		},
		overwritingPriceInfoAggregate: {
			type: 'object',
			nullable: true,
			properties: {},
			additionalProperties: true,
		},
		bookingStatus: {
			type: 'string',
			enum: [
				'NEW',
				'EXECUTING',
				'FINISHED',
				'CANCELLED_BY_DISTRIBUTOR',
				'CANCELLED_BY_PASSENGER',
				'CANCELLED_BY_DRIVER',
				'CANCELLED_NO_SHOW',
			],
		},
		updatedTime: { type: 'string' },
		cancellationReason: { type: 'string', nullable: true },
		cancellableUntil: { type: 'string', nullable: true },
		toddlerSeats: { type: 'number' },
		bulkyLuggage: { type: 'boolean' },
		paymentState: { type: 'string', nullable: true },
		usedVoucher: {
			type: 'object',
			nullable: true,
			properties: {},
			additionalProperties: true,
		},
	},
	required: ['bookingNumber', 'updatedTime', 'id', 'bookingCode'],
} as const;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used only via typeof
const schemaTyped = toTypedRxJsonSchema(passengerBookingSchemaLiteral);

export type TPassengerBookingDocType = ExtractDocumentTypeFromTypedRxJsonSchema<
	typeof schemaTyped
>;

/** Bump to re-pull already-synced bookings once, e.g. after BE adds a field. */
export const PASSENGER_BOOKING_SYNC_VERSION = 1;

export interface IPassengerBookingCheckpoint {
	bookingId: string;
	updateTimestamp: string;
	/** Absent = written before versioning; outdated. */
	syncVersion?: number;
}

// create the typed RxJsonSchema from the literal typed object.
export const passengerBookingSchema: RxJsonSchema<TPassengerBookingDocType> =
	passengerBookingSchemaLiteral;
